import React from "react";
import { BookOpen, Lock } from "lucide-react";
import { THESIS_SECTIONS } from "./ThesisEditor";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);

const VERDICT = {
  intact: { label: "Intact", cls: "bg-emerald-500/15 text-emerald-600" },
  weakening: { label: "Weakening", cls: "bg-amber-500/15 text-amber-600" },
  broken: { label: "Broken", cls: "bg-red-500/15 text-red-600" },
};

function RecheckStrip({ rechecks }) {
  const list = (rechecks || []).filter((r) => VERDICT[r.verdict]);
  if (!list.length) return null;
  const latest = list[list.length - 1];
  const v = VERDICT[latest.verdict];
  return (
    <div className="px-6 py-4 border-b border-mb-border" data-testid="thesis-recheck">
      <div className="flex flex-wrap items-center gap-2">
        <span className="label-uppercase text-neutral-500">Daily re-check</span>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${v.cls}`} data-testid="thesis-recheck-verdict">{v.label}</span>
        <span className="text-[11px] text-neutral-500">{fmtDate(latest.generated_at || `${latest.date}T21:00:00Z`)}</span>
        {list.length > 1 && (
          <span className="flex gap-1 ml-auto" title="Earlier re-checks, oldest first" data-testid="thesis-recheck-history">
            {list.slice(-10).map((r, i) => (
              <span key={`${r.date}-${i}`} className={`w-2 h-2 rounded-full ${r.verdict === "intact" ? "bg-emerald-500" : r.verdict === "weakening" ? "bg-amber-500" : "bg-red-500"}`} />
            ))}
          </span>
        )}
      </div>
      <p className="mt-1.5 text-sm text-neutral-800" data-testid="thesis-recheck-note">{latest.note}</p>
    </div>
  );
}

// Subscriber-facing entry thesis, frozen at publish, plus the daily re-check.
export default function EntryThesisCard({ thesis, rechecks }) {
  if (!thesis?.the_read) return null;
  const locked = fmtDate(thesis.locked_at);
  return (
    <section id="thesis" className="mt-6 bg-white border border-mb-border rounded-lg overflow-hidden scroll-mt-24" data-testid="entry-thesis-card">
      <div className="px-6 py-4 border-b border-mb-border flex items-center justify-between gap-3">
        <div>
          <div className="label-uppercase text-neutral-500">Entry thesis</div>
          <div className="text-sm text-neutral-700 mt-0.5">Why the desk published this alert</div>
        </div>
        <BookOpen className="w-5 h-5 text-mb-emerald flex-shrink-0" />
      </div>
      <RecheckStrip rechecks={rechecks} />
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 divide-mb-border">
        {THESIS_SECTIONS.map((s, i) => (
          <div key={s.key} className={`p-6 ${i >= 2 ? "md:border-t md:border-mb-border" : ""} ${i % 2 === 1 ? "md:border-l md:border-mb-border" : ""}`} data-testid={`thesis-section-${s.key}`}>
            <div className={`label-uppercase ${s.key === "what_breaks" ? "text-red-600" : "text-mb-emerald"}`}>{s.label}</div>
            <p className="mt-2 text-sm text-neutral-800 leading-relaxed">{thesis[s.key]}</p>
          </div>
        ))}
      </div>
      <div className="px-6 py-3 border-t border-mb-border flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-neutral-500">
        {locked && (
          <span className="inline-flex items-center gap-1" data-testid="thesis-locked-at">
            <Lock className="w-3 h-3" /> Locked at publish · {locked}
          </span>
        )}
        <span>Educational research describing what we see — not personal investment advice.</span>
      </div>
    </section>
  );
}
